import React, { useEffect, useMemo } from "react";
import _ from "lodash";
import Backgrounds from "../../../_partials/Backgrounds/Index";
import TemplateBlock from "../../../_partials/TemplateBlock/Index";
import prepareVideoBg from "../../../../../shared/functions/prepareVideoBg";
import animationScript, {
  animationCleanUp,
} from "../../../../../../animations/worldcup/matchdayoverview";
import assets from "../assets";
import { useTranslation } from "react-i18next";
import ImageComponent from "../../../../../shared/components/Image";
import ProgressBarComponent from "../../../../../shared/components/ProgressBar";
import MatchRow from "./MatchRow.jsx";

const Image = ImageComponent?.default || ImageComponent;
const ProgressBar = ProgressBarComponent?.default || ProgressBarComponent;

const STAGE_UI_CONFIG = {
  R32: { cssClass: "Roundof32", legacyName: "Round of 32" },
  R16: { cssClass: "Roundof16", legacyName: "Round of 16" },
  QF: { cssClass: "Quarter-finals", legacyName: "Quarter-finals" },
  SF: { cssClass: "Semi-finals", legacyName: "Semi-finals" },
  FINAL: { cssClass: "Final", legacyName: "Final", variant: "final" },
  "3RD": { cssClass: "3rdPlaceFinal", legacyName: "3rd Place Final", variant: "third" },
};

const RENDER_STAGES = ["R32", "R16", "QF", "SF", "FINAL", "3RD"];

function adaptTreeMatch(match, stageKey) {
  const homeCode = match?.home?.code || match?.home?.shortcut || null;
  const awayCode = match?.away?.code || match?.away?.shortcut || null;
  const hasKnownTeams = Boolean(match?.home?.id && match?.away?.id);
  const hasScore =
    match?.finished ||
    (Number(match?.score?.home) + Number(match?.score?.away) > 0);
  const isPlaying =
    match?.status === "playing" ||
    match?.actual_time_name === "live" ||
    match?.actual_time_alias === "live";

  return {
    id: match?.eventId || match?.matchId,
    matchInfo: {
      id: match?.eventId || match?.matchId,
      nextMatchWinnerId: match?.nextMatchId || null,
      date: match?.schedule?.date || null,
      time: match?.schedule?.time || null,
      plannedKickoffTime: match?.schedule?.plannedKickoffTime || null,
      contestant: [
        {
          id: match?.home?.id || null,
          name: match?.home?.name || "tbd.",
          code: homeCode || "tbd.",
        },
        {
          id: match?.away?.id || null,
          name: match?.away?.name || "tbd.",
          code: awayCode || "tbd.",
        },
      ],
      stage: { name: STAGE_UI_CONFIG[stageKey]?.legacyName },
      result: match?.result || null,
    },
    liveData: {
      matchDetails: {
        matchStatus: isPlaying
          ? "playing"
          : hasScore || match?.finished
            ? "played"
            : "fixture",
        scores: {
          total: {
            home: match?.score?.home ?? 0,
            away: match?.score?.away ?? 0,
          },
        },
      },
    },
    _tree: match,
  };
}

function getSortedStagesFromTournamentTree(tournamentTree) {
  if (!tournamentTree?.matches?.length) {
    return null;
  }

  const sortedStages = {};

  RENDER_STAGES.forEach((stageKey) => {
    const config = STAGE_UI_CONFIG[stageKey];
    const stageMatches = tournamentTree.matches.filter(
      (match) => match.stage === stageKey,
    );

    sortedStages[config.legacyName] = stageMatches.map((match) =>
      adaptTreeMatch(match, stageKey),
    );
  });

  return sortedStages;
}

function getTournamentTitle(tournamentTree, competition) {
  return (
    tournamentTree?.tournament?.name ||
    competition?.name ||
    "World Cup"
  );
}

function getRenderStages(tournamentTree) {
  if (!tournamentTree?.matches?.length) {
    return [];
  }

  return RENDER_STAGES.map((stageKey) => ({
    stageKey,
    ...STAGE_UI_CONFIG[stageKey],
    matches: tournamentTree.matches
      .filter((match) => match.stage === stageKey)
      .map((match) => adaptTreeMatch(match, stageKey)),
  }));
}

require("./style.scss");
require("../style/animation.scss");

function isCopa(id) {
  return id === "45db8orh1qttbsqq9hqapmbit";
}

function competitionTitle(competition, tournamentTree) {
  const treeTitle = getTournamentTitle(tournamentTree, competition);
  const id = competition?.id;

  if (id === "45db8orh1qttbsqq9hqapmbit") {
    return "COPA America";
  }
  if (id === "8tddm56zbasf57jkkay4kbf11") {
    return "Euro 2024";
  }
  if (id === "70excpe1synn9kadnbppahdn7") {
    return "World Cup";
  }

  return treeTitle || competition?.name || "World Cup";
}

function groupLegacyMatchdays(matchdays) {
  return _.groupBy(matchdays, (match) => match?.matchInfo?.stage?.name);
}

function pairLegacyMatches(currentStageMatches, previousStageMatches) {
  const groups = {};

  previousStageMatches.forEach((match) => {
    const winnerId = match?.matchInfo?.nextMatchWinnerId;
    if (!winnerId) {
      return;
    }
    if (!groups[winnerId]) {
      groups[winnerId] = [];
    }
    groups[winnerId].push(match);
  });

  return Object.keys(groups)
    .map((winnerId) =>
      currentStageMatches.find((match) => match?.matchInfo?.id === winnerId),
    )
    .filter(Boolean);
}

function buildLegacySortedStages(groupByStage) {
  const sortedStages = {
    "8th Finals": groupByStage?.["8th Finals"] || [],
    "Round of 16": groupByStage?.["Round of 16"] || [],
    "Quarter-finals": groupByStage?.["Quarter-finals"] || [],
    "Semi-finals": groupByStage?.["Semi-finals"] || [],
    Final: groupByStage?.["Final"] || [],
    "3rd Place Final": groupByStage?.["3rd Place Final"] || [],
  };

  if (sortedStages["Quarter-finals"].length) {
    sortedStages["Quarter-finals"] = pairLegacyMatches(
      sortedStages["Quarter-finals"],
      sortedStages["8th Finals"].length
        ? sortedStages["8th Finals"]
        : sortedStages["Round of 16"],
    );
  }

  if (sortedStages["Semi-finals"].length) {
    sortedStages["Semi-finals"] = pairLegacyMatches(
      sortedStages["Semi-finals"],
      sortedStages["Quarter-finals"],
    );
  }

  if (sortedStages.Final?.length) {
    sortedStages.Final = pairLegacyMatches(
      sortedStages.Final,
      sortedStages["Semi-finals"],
    );
  }

  if (sortedStages["3rd Place Final"]?.length) {
    sortedStages["3rd Place Final"] = pairLegacyMatches(
      sortedStages["3rd Place Final"],
      sortedStages["Semi-finals"],
    );
  }

  return sortedStages;
}

const LEGACY_STAGE_ROWS = [
  { legacyName: "Round of 32", cssClass: "Roundof32", variant: "default" },
  { legacyName: "8th Finals", cssClass: "Roundof32", variant: "default" },
  { legacyName: "Round of 16", cssClass: "Roundof16", variant: "default" },
  { legacyName: "Quarter-finals", cssClass: "Quarter-finals", variant: "default" },
  { legacyName: "Semi-finals", cssClass: "Semi-finals", variant: "default" },
  { legacyName: "Final", cssClass: "Final", variant: "final" },
  {
    legacyName: "3rd Place Final",
    cssClass: "3rdPlaceFinal",
    variant: "third",
  },
];

const MatchDayOverView2 = (props) => {
  const refs = {
    player: React.createRef(),
    team: React.createRef(),
  };

  const { animation, data, mode, format, apiSetup } = props;
  const { t } = useTranslation();

  const id = `${_.get(data, "template_name", "")}_${_.get(data, "uuid", "")}`;

  useEffect(() => {
    let animationReturn = null;
    if (mode === "animation" || mode === "video") {
      if (animation) {
        prepareVideoBg({ id });
        animationReturn = animationScript({
          refs,
          data,
          animation,
          mode,
          id,
          format,
          apiSetup,
          templateName: data?.template_name,
        });
      }
    }

    return function cleanUp() {
      if (animationReturn) {
        animationCleanUp(animationReturn);
      }
    };
  }, [animation, mode]);

  const templateData = _.get(data, "data");
  const { dynamic } = templateData || {};
  const matchInfo = _.get(dynamic, "matchInfo");
  const matchdays = _.get(dynamic, "matchdays");
  const tournamentTree = _.get(dynamic, "tournamentTree");
  const competition = matchInfo?.competition;
  const competitionId =
    dynamic?.competitionId ||
    competition?.id ||
    tournamentTree?.tournament?.competitionId ||
    "DFL-COM-J00028";

  const is_copa = isCopa(competitionId);
  const showDebug = Boolean(dynamic?.debugBracket);

  const renderStages = useMemo(() => {
    if (tournamentTree?.matches?.length) {
      return getRenderStages(tournamentTree);
    }

    const groupByStage = groupLegacyMatchdays(matchdays);
    const sortedStages = buildLegacySortedStages(groupByStage);

    return LEGACY_STAGE_ROWS.map((stage) => ({
      stageKey: stage.legacyName,
      cssClass: stage.cssClass,
      legacyName: stage.legacyName,
      variant: stage.variant,
      matches: sortedStages[stage.legacyName] || [],
    })).filter((stage) => stage.matches.length > 0);
  }, [tournamentTree, matchdays]);

  const sortedStages = useMemo(() => {
    if (tournamentTree?.matches?.length) {
      return getSortedStagesFromTournamentTree(tournamentTree);
    }
    return buildLegacySortedStages(groupLegacyMatchdays(matchdays));
  }, [tournamentTree, matchdays]);

  const backgroundImage = _.get(assets, "background_1920");
  const hasRoundOf32 = Boolean(
    sortedStages?.["Round of 32"]?.length || sortedStages?.["8th Finals"]?.length,
  );

  return (
    <div
      id={id}
      className={`T_${_.get(data, "template_name", "")}_2 ${
        animation ? "animation" : ""
      } side-${_.get(dynamic, "teamSide")}`}
    >
      <TemplateBlock>
        <Backgrounds bg1={backgroundImage} loop={true} />

        <div className="wc-content wc-content-v2">
          {ProgressBar ? <ProgressBar height="8px" /> : null}
          <div className="wc-bg-content wc-bg-content--opasity" />

          <div className="sub-header">{t("Tournament tree")}</div>
          <div className="main-header green-glow">
            {competitionTitle(competition, tournamentTree)}
          </div>

          <div className="left-lines">
            <div className="final-line final-line-1" />
            <div className="final-line final-line-2" />

            {hasRoundOf32 && !is_copa ? (
              <>
                <div className="line line1" />
                <div className="line line2" />
                <div className="line line3" />
                <div className="line line4" />
              </>
            ) : null}
            <div className="line line5" />
            <div className="line line6" />
          </div>

          <div className="right-lines">
            {hasRoundOf32 && !is_copa ? (
              <>
                <div className="line line1" />
                <div className="line line2" />
                <div className="line line3" />
                <div className="line line4" />
              </>
            ) : null}
            <div className="line line5" />
            <div className="line line6" />
          </div>

          <div className="group-table">
            <div className="wc-content-header" />
            <div className="wc-content-table">
              {renderStages.map((stage) => (
                <div
                  key={stage.stageKey}
                  className={`group-rows stage-${stage.cssClass.replace(/\s/g, "")}`}
                >
                  {stage.matches.map((matchday, index) => (
                    <MatchRow
                      key={matchday?.id || `${stage.stageKey}-${index}`}
                      matchday={matchday}
                      index={index}
                      competitionId={competitionId}
                      variant={stage.variant || "default"}
                      showDebug={showDebug}
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </TemplateBlock>
    </div>
  );
};

export default MatchDayOverView2;
